import { KeyboardEvent, useEffect, useMemo, useState, FC } from 'react';
import { useGridDataFromServer } from '@chargeafter/providers';
import { Typography } from '@chargeafter/ui';
import { styled } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { ConsumersTabSearchIcon } from '../icons';
import { useIntl } from 'react-intl';
import {
  useSetConsumerId,
  useSetViewedConsumerRowDetails,
  ConsumerRowDetails,
  useGetConsumersListRefresher,
  useSetAccountsRefresher,
  useSetPendingAmountRefresher,
} from '../services/customersTabManager';
import {
  useGetMerchantCurrency,
  useGetSearchConsumersAcrossAllStores,
} from '../services/configManager';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box } from '@mui/system';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useGetColumns } from '../services/tableManager';
import { addToContext } from '../services/contextManager';
import { useGetStoreId } from '../services/storeManager';
import { ButtonName } from '../constants';
import { useApplyStartOver } from '../services/flowManager';
import { DataGrid, ColumnMeta } from '@chargeafter/data-grid';
import {
  MuiTrackerButton,
  TrackerIconButton,
} from '../components/TrackerButtonWrapper';
import { useUpdateConfigurationHandler } from '../services/lendersConfigurationManager';
import StatusChip from '../components/StatusChip';
import { useSetCurrentModal } from '../services/modalManager';
import { NoData } from '../components/NoData';

const Container = styled('div')({
  padding: '24px 24px 0 24px',
});

const HeaderContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: 16,
});

const StyledTextField = styled(TextField)({
  minWidth: 384,
  height: 48,
  '& .MuiOutlinedInput-root': {
    height: 48,
    padding: 0,
  },
});

const StyledSearchButton = styled(MuiTrackerButton)(({ theme }) => ({
  padding: 15,
  margin: 0,
  borderRadius: 5,
  '&:hover, &:active': {
    backgroundColor: theme.palette.secondary.light,
  },
}));

const StyledHeader = styled(Typography)({
  alignSelf: 'center',
});

type ConsumersTabProps = {
  isPendingTab?: boolean;
};

const StyledPendingCell = styled(Box)({
  display: 'flex',
  gap: '10px',
});
const ConsumersTab: FC<ConsumersTabProps> = ({ isPendingTab }) => {
  const [searchedValue, setSearchedValue] = useState('');
  const [search, setSearch] = useState('');
  const storeId = useGetStoreId();
  const updatePendingAmountRefresher = useSetPendingAmountRefresher();
  const intl = useIntl();
  const searchConsumersAcrossAllStores = useGetSearchConsumersAcrossAllStores();
  const currency = useGetMerchantCurrency();
  const { columns, loadServerRows } = useGridDataFromServer({
    collection: 'merchantConsumersData',
    getColumns: useGetColumns,
    customQueryInput: {
      inputFilter: {
        textFilter: search ?? undefined,
        storeId:
          !isPendingTab && search && searchConsumersAcrossAllStores
            ? null
            : storeId,
        statuses: isPendingTab
          ? ['CheckoutInProgress', 'PendingConsumer', 'PendingLender']
          : null,
      },
    },
  });

  useEffect(() => {
    setSearchedValue('');
    setSearch('');
    if (isPendingTab) updatePendingAmountRefresher((n) => n + 1);
  }, [isPendingTab]);

  const newColumns = useMemo(() => {
    return columns.map((col) => {
      const newColumn = { ...col };
      if (col.field === 'availableAmount') {
        newColumn.renderCell = (params: GridRenderCellParams) => {
          const { value } = params;
          const status = params.row.status;
          return (
            <Box data-testid="available-amount-col">
              {value > 0 ? (
                <>
                  <StyledPendingCell>
                    <Box width="fit-content">
                      {intl.formatNumber(value, {
                        style: 'currency',
                        currency: currency,
                        currencySign: 'standard',
                      })}
                    </Box>
                    <StatusChip status={status} />
                  </StyledPendingCell>
                </>
              ) : (
                'N/A'
              )}
            </Box>
          );
        };
      }
      return newColumn;
    });
  }, [columns]);

  const setColumnWidth = (col: ColumnMeta) => {
    if (col.field === 'updated') return Object({ ...col, minWidth: 200 });
    else if (col.field === 'mobilePhoneNumber')
      return Object({ ...col, minWidth: 150 });
    else if (col.field === 'availableAmount')
      return Object({
        ...col,
        minWidth: 250,
        headerAlign: 'left',
        align: 'left',
      });
    return Object({ ...col, minWidth: 120 });
  };

  const updateCurrentModal = useSetCurrentModal();
  const updateViewedConsumerDetails = useSetViewedConsumerRowDetails();
  const updateConsumerId = useSetConsumerId();
  const consumersTableRefresher = useGetConsumersListRefresher();
  const applyStartOver = useApplyStartOver();
  const updateAccountsRefresher = useSetAccountsRefresher();
  const updateConfigurationHandler = useUpdateConfigurationHandler();

  const handleConsumer = async (consumerRowDetails: ConsumerRowDetails) => {
    updateAccountsRefresher((n) => n + 1);
    updateConfigurationHandler();
    applyStartOver(false);
    updateConsumerId(consumerRowDetails.id);
    updateViewedConsumerDetails(consumerRowDetails);
    updateCurrentModal('consumerAccounts');
    addToContext('consumer', consumerRowDetails);
  };

  const handleSearchChange = (str: string) => {
    setSearchedValue(str);
  };

  const handleOnSearch = () => {
    setSearch(searchedValue);
  };

  const onReturnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Enter') {
      (e.target as HTMLInputElement).blur();
      handleOnSearch();
    }
  };

  return (
    <Container>
      <HeaderContainer>
        <StyledHeader variant="h5">
          Select a customer from the list below
        </StyledHeader>
        <StyledTextField
          data-testid="search"
          onKeyDown={onReturnKeyDown}
          placeholder="Search by email or phone number"
          onChange={(e) => handleSearchChange(e.target.value)}
          value={searchedValue}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <StyledSearchButton
                  buttonName={ButtonName.SearchConsumer}
                  onClick={handleOnSearch}
                >
                  <ConsumersTabSearchIcon
                    color="primary"
                    sx={{ width: 18, height: 18 }}
                  />
                </StyledSearchButton>
              </InputAdornment>
            ),
          }}
        />
      </HeaderContainer>
      <div style={{ height: 'calc(100vh - 238px)' }}>
        {useMemo(
          () => (
            <DataGrid
              gridPreLoader={false}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'updated', sort: 'desc' }],
                },
              }}
              noDataComponent={<NoData type="consumers" />}
              shouldPaginate
              columns={newColumns.map((col) => setColumnWidth(col))}
              loadServerRows={loadServerRows}
              toolbar={null}
              reloadToken={consumersTableRefresher}
              customColumn={{
                pinned: true,
                headerName: '',
                minWidth: 50,
                width: 50,
                component: ({ rowData }) => (
                  <TrackerIconButton
                    buttonName={ButtonName.ToConsumerDetails}
                    color="inherit"
                    size="small"
                    onClick={() => handleConsumer(rowData)}
                  >
                    <ChevronRightRoundedIcon
                      color="primary"
                      fontSize="medium"
                    />
                  </TrackerIconButton>
                ),
              }}
            />
          ),
          [search, isPendingTab, consumersTableRefresher, storeId],
        )}
      </div>
    </Container>
  );
};

export default ConsumersTab;
